import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Flex } from 'rebass';
import { Trans } from '@lingui/macro';
import { Typography, IconButton } from '@oca/ui';
import { Delete } from '@oca/icons';

/**
 * Shared card wrapper for every entry inside the Documents step: identity
 * documents, bank loans, contracts, education. Keeps a consistent header
 * (title + required/optional tag + remove action) and a padded body for
 * the fields.
 */
export function DocumentCard({ title, tag, onRemove, children }) {
  return (
    <CardShell>
      <Header>
        <Flex alignItems="center" flexWrap="wrap" style={{ gap: 10 }}>
          <Typography fontSize={15} fontWeight={600} color="#181D27">
            {title}
          </Typography>
          {tag}
        </Flex>
        {onRemove && (
          <IconButton
            type="button"
            color="error"
            size="small"
            aria-label="Remove"
            onClick={onRemove}
          >
            <Delete />
          </IconButton>
        )}
      </Header>
      <Body>{children}</Body>
    </CardShell>
  );
}

DocumentCard.propTypes = {
  title: PropTypes.node.isRequired,
  tag: PropTypes.node,
  onRemove: PropTypes.func,
  children: PropTypes.node.isRequired,
};

DocumentCard.defaultProps = {
  tag: null,
  onRemove: undefined,
};

export function RequiredTag() {
  return (
    <Tag color="#B91C1C" bg="#FEF2F2">
      <Trans>Required</Trans>
    </Tag>
  );
}

export function OptionalTag() {
  return (
    <Tag color="#475569" bg="#F1F5F9">
      <Trans>Optional</Trans>
    </Tag>
  );
}

/* Numbered chip for ordered entries like "Contract #2". */
export function IndexBadge({ children }) {
  return <Badge>#{children}</Badge>;
}
IndexBadge.propTypes = {
  children: PropTypes.node.isRequired,
};

const CardShell = styled(Box)`
  background: #ffffff;
  border: 1px solid #e9eaeb;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: visible;
  position: relative;

  &:focus-within {
    z-index: 10;
  }
`;

const Header = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  background: #fafbfc;
  border-bottom: 1px solid #eef1f4;
  border-top-left-radius: 9px;
  border-top-right-radius: 9px;
`;

const Body = styled(Box)`
  padding: 12px 16px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ color }) => color};
  background: ${({ bg }) => bg};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
`;
